/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { FiCheckCircle, FiLoader, FiX } from 'react-icons/fi';
import {
  PASSENGER_TYPE_LABEL,
  daysNightsText,
  formatPrice,
  paxSummary,
  paxTypeLabel,
} from '../../utils/packageUtils';
import {
  WALLET_MODE,
  paymentModeLabel,
} from '../../constants/packageConstants';
import './PackageReviewModal.scss';

function Row({ label, value }) {
  return (
    <div className="pkg-review-modal__row">
      <span>{label}</span>
      <strong>{value || '—'}</strong>
    </div>
  );
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

Row.defaultProps = { value: '' };

/** Last look before the wallet is debited. Can't be dismissed while booking. */
function PackageReviewModal({
  show,
  onHide,
  onConfirm,
  submitting,
  summary,
  tripLine,
  pax,
  passengers,
  requests,
  fare,
  paymentMode,
  pgName,
}) {
  const online = paymentMode !== WALLET_MODE;
  const currency = fare?.currency || 'INR';
  const total = Number(fare?.grandTotal) || 0;
  const breakdown = Array.isArray(fare?.breakdown)
    ? fare.breakdown.filter((b) => b?.roomType)
    : [];

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      size="lg"
      centered
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
      dialogClassName="pkg-modal pkg-review-modal"
    >
      <Modal.Body>
        {!submitting && (
          <button
            type="button"
            className="pkg-modal__close"
            onClick={onHide}
            aria-label="Close"
          >
            <FiX />
          </button>
        )}

        <header className="pkg-review-modal__head">
          <span className="pkg-review-modal__eyebrow">
            Review &amp; Confirm
          </span>
          <h3>Confirm your package booking</h3>
          <p>
            Check the details below.{' '}
            {online
              ? 'When you continue you are taken to the payment gateway; the booking is confirmed once the payment succeeds.'
              : 'When you confirm, the total is debited from your wallet and the booking is sent to the supplier.'}
          </p>
        </header>

        <section className="pkg-review-modal__section">
          <h4>Package</h4>
          <div className="pkg-review-modal__package">
            <div>
              <strong>{summary.name}</strong>
              <small>
                {[
                  summary.destination,
                  daysNightsText(summary.days, summary.nights),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </small>
            </div>
            <div className="pkg-review-modal__price">
              <span>{formatPrice(total, currency)}</span>
              <small>total</small>
            </div>
          </div>
          <div className="pkg-review-modal__rows">
            <Row label="Travel dates" value={tripLine} />
            <Row
              label="Joining city · grade"
              value={[summary.city, summary.grade].filter(Boolean).join(' · ')}
            />
            <Row label="Travellers" value={paxSummary(pax)} />
            {breakdown.map((b) => (
              <Row
                key={b.roomType}
                label={`${paxTypeLabel(b.roomType)} · ${formatPrice(
                  b.pricePp,
                  currency,
                )} × ${b.paxCount}`}
                value={formatPrice(b.amount, currency)}
              />
            ))}
          </div>
        </section>

        <section className="pkg-review-modal__section">
          <h4>Travellers</h4>
          <ul className="pkg-review-modal__people">
            {passengers.map((p, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>
                <span className="pkg-review-modal__num">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="pkg-review-modal__person">
                  <strong>
                    {[p.title, p.firstName.trim(), p.lastName.trim()].join(' ')}
                  </strong>
                  <small>
                    {[
                      PASSENGER_TYPE_LABEL[p.type],
                      `${p.age} yrs`,
                      i === 0 && p.email,
                      i === 0 && `${p.dialCode} ${p.mobile}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </span>
                {i === 0 && <em>Lead</em>}
              </li>
            ))}
          </ul>
        </section>

        <section className="pkg-review-modal__section">
          <h4>Payment</h4>
          <div className="pkg-review-modal__rows">
            <Row label="Payment method" value={paymentModeLabel(paymentMode)} />
            {online && <Row label="Payment gateway" value={pgName} />}
          </div>
        </section>

        {requests.trim() && (
          <section className="pkg-review-modal__section">
            <h4>Special requests</h4>
            <p className="pkg-review-modal__note">{requests.trim()}</p>
          </section>
        )}
      </Modal.Body>

      <Modal.Footer className="pkg-review-modal__footer">
        <button
          type="button"
          className="pkg-review-modal__cancel"
          onClick={onHide}
          disabled={submitting}
        >
          Edit details
        </button>
        <button
          type="button"
          className="pkg-review-modal__confirm"
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <FiLoader className="pkg-spin" />{' '}
              {online
                ? 'Opening payment… do not close'
                : 'Booking… do not close'}
            </>
          ) : (
            <>
              <FiCheckCircle /> {online ? 'Proceed to Pay' : 'Confirm & Pay'}{' '}
              {formatPrice(total, currency)}
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

PackageReviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  summary: PropTypes.object.isRequired,
  tripLine: PropTypes.string,
  pax: PropTypes.object.isRequired,
  passengers: PropTypes.array.isRequired,
  requests: PropTypes.string,
  fare: PropTypes.object,
  paymentMode: PropTypes.string,
  pgName: PropTypes.string,
};

PackageReviewModal.defaultProps = {
  submitting: false,
  tripLine: '',
  requests: '',
  fare: null,
  paymentMode: WALLET_MODE,
  pgName: '',
};

export default PackageReviewModal;
